export interface MoodOption {
  id: string;
  label: string;
  emoji: string;
  zone: 'blue' | 'green' | 'yellow' | 'red';
}

export interface MoodZone {
  id: 'blue' | 'green' | 'yellow' | 'red';
  label: string;
  description: string;
  colorClass: string;
  bgClass: string;
  borderClass: string;
  moods: MoodOption[];
}

export interface PhysicalStateOption {
  id: string;
  label: string;
  icon: string;
}

export interface BelongingOption {
  id: string;
  label: string;
  icon: string;
}

export interface CannedMessage {
  id: string;
  text: string;
}

export type NotificationChannel = 'whatsapp' | 'sms' | 'email' | 'app';

export interface PreInspectionLog {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  date: string;
  timestamp: string;
  moodId: string;
  moodZone: 'blue' | 'green' | 'yellow' | 'red';
  physicalStates: string[];
  belongings: string[];
  notes: string;
  notifyParent: boolean;
  notificationChannels: NotificationChannel[];
  notificationSent: boolean;
  createdBy: string;
}

export const moodZones: MoodZone[] = [
  {
    id: 'blue',
    label: 'Low Energy / Down',
    description: 'Student is running slow or feeling withdrawn',
    colorClass: 'bg-blue-500',
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-300',
    moods: [
      { id: 'sad', label: 'Sad / Tearful', emoji: '😢', zone: 'blue' },
      { id: 'tired', label: 'Tired / Lethargic', emoji: '😴', zone: 'blue' },
      { id: 'withdrawn', label: 'Withdrawn / Unresponsive', emoji: '😶', zone: 'blue' },
      { id: 'sick', label: 'Sick / Physically Unwell', emoji: '🤒', zone: 'blue' },
    ],
  },
  {
    id: 'green',
    label: 'Regulated / Ready',
    description: 'Student is calm and ready to participate',
    colorClass: 'bg-green-500',
    bgClass: 'bg-green-50',
    borderClass: 'border-green-300',
    moods: [
      { id: 'calm', label: 'Calm / Neutral', emoji: '🙂', zone: 'green' },
      { id: 'happy', label: 'Happy / Content', emoji: '😊', zone: 'green' },
      { id: 'focused', label: 'Focused / Attentive', emoji: '🧠', zone: 'green' },
      { id: 'cooperative', label: 'Cooperative / Engaged', emoji: '🤝', zone: 'green' },
    ],
  },
  {
    id: 'yellow',
    label: 'Heightened Energy / Unsettled',
    description: 'Student is experiencing elevated stress or loss of some control',
    colorClass: 'bg-yellow-400',
    bgClass: 'bg-yellow-50',
    borderClass: 'border-yellow-300',
    moods: [
      { id: 'anxious', label: 'Anxious / Nervous', emoji: '😰', zone: 'yellow' },
      { id: 'restless', label: 'Restless / Fidgety', emoji: '🦵', zone: 'yellow' },
      { id: 'frustrated', label: 'Frustrated / Irritated', emoji: '😤', zone: 'yellow' },
      { id: 'overstimulated', label: 'Overstimulated / Sensory-Seeking', emoji: '🔄', zone: 'yellow' },
    ],
  },
  {
    id: 'red',
    label: 'High Energy / Extreme',
    description: 'Student is experiencing intense emotions or complete loss of control',
    colorClass: 'bg-red-500',
    bgClass: 'bg-red-50',
    borderClass: 'border-red-300',
    moods: [
      { id: 'angry', label: 'Angry / Aggressive', emoji: '😡', zone: 'red' },
      { id: 'elated', label: 'Elated / Out of Control', emoji: '🤪', zone: 'red' },
      { id: 'distressed', label: 'Distressed / Panicked', emoji: '😱', zone: 'red' },
      { id: 'meltdown', label: 'Meltdown / Crisis State', emoji: '💥', zone: 'red' },
    ],
  },
];

export const allMoods: MoodOption[] = moodZones.flatMap((z) => z.moods);

export function getMoodById(id: string): MoodOption | undefined {
  return allMoods.find((m) => m.id === id);
}

export function getZoneById(id: string): MoodZone | undefined {
  return moodZones.find((z) => z.id === id);
}

export const physicalStateOptions: PhysicalStateOption[] = [
  { id: 'normal', label: 'No visible marks / Normal', icon: 'ri-check-line' },
  { id: 'scratch', label: 'Minor scratch / mark', icon: 'ri-contrast-drop-line' },
  { id: 'fresh-bruise', label: 'Fresh bruise', icon: 'ri-emotion-sad-line' },
  { id: 'old-bruise', label: 'Old bruise / healing', icon: 'ri-hearts-line' },
  { id: 'bandage', label: 'Bandage / dressing present', icon: 'ri-first-aid-kit-line' },
  { id: 'rash', label: 'Rash / skin irritation', icon: 'ri-alert-line' },
  { id: 'other-physical', label: 'Other visible concern', icon: 'ri-more-line' },
];

export const belongingOptions: BelongingOption[] = [
  { id: 'school-bag', label: 'School bag', icon: 'ri-briefcase-line' },
  { id: 'lunch-box', label: 'Lunch box', icon: 'ri-restaurant-line' },
  { id: 'water-bottle', label: 'Water bottle', icon: 'ri-cup-line' },
  { id: 'change-clothes', label: 'Change of clothes', icon: 'ri-shirt-line' },
  { id: 'medication', label: 'Medication', icon: 'ri-medicine-bottle-line' },
  { id: 'comm-diary', label: 'Communication diary', icon: 'ri-book-open-line' },
  { id: 'homework', label: 'Homework / notebook', icon: 'ri-file-text-line' },
  { id: 'other-belonging', label: 'Other item', icon: 'ri-more-line' },
];

export const cannedMessages: CannedMessage[] = [
  { id: 'canned-1', text: 'Student arrived crying, parent states they did not sleep well last night' },
  { id: 'canned-2', text: 'Student appears tired and unresponsive this morning' },
  { id: 'canned-3', text: 'Visible bruise on right arm, parent informed during handover' },
  { id: 'canned-4', text: 'Student has a slight fever, will monitor throughout the day' },
  { id: 'canned-5', text: 'Student brought medication, will administer at scheduled time' },
  { id: 'canned-6', text: 'Student appears happy and well-rested, parent reports good night' },
  { id: 'canned-7', text: 'Student had breakfast at home before pickup' },
  { id: 'canned-8', text: 'Student is excited and ready for the day ahead' },
  { id: 'canned-9', text: 'Parent mentioned student had a difficult morning at home' },
  { id: 'canned-10', text: 'Student was cooperative during transit, no concerns noted' },
];

export function getTodayStr(): string {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

export function getNowTimestamp(): string {
  return new Date().toISOString();
}

function generateDateStr(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

export const preInspectionLogs: PreInspectionLog[] = [
  {
    id: 'pi-001',
    studentId: 'STU-2024-001',
    studentName: 'Aarav Mehta',
    className: 'Pre Primary - A',
    date: generateDateStr(0),
    timestamp: `${generateDateStr(0)}T08:15:00.000Z`,
    moodId: 'calm',
    moodZone: 'green',
    physicalStates: ['normal'],
    belongings: ['school-bag', 'lunch-box', 'water-bottle'],
    notes: 'Arrived on time, parent reported good night sleep',
    notifyParent: false,
    notificationChannels: [],
    notificationSent: false,
    createdBy: 'Ms. Emily Watson',
  },
  {
    id: 'pi-002',
    studentId: 'STU-2024-004',
    studentName: 'Zara Rodriguez',
    className: 'Primary - B',
    date: generateDateStr(0),
    timestamp: `${generateDateStr(0)}T08:22:00.000Z`,
    moodId: 'tired',
    moodZone: 'blue',
    physicalStates: ['normal'],
    belongings: ['school-bag', 'water-bottle', 'medication'],
    notes: 'Student arrived looking tired, parent states they did not sleep well',
    notifyParent: true,
    notificationChannels: ['whatsapp', 'email'],
    notificationSent: true,
    createdBy: 'Mr. James Okafor',
  },
  {
    id: 'pi-003',
    studentId: 'STU-2024-006',
    studentName: 'Sofia Nakamura',
    className: 'Pre Primary - A',
    date: generateDateStr(1),
    timestamp: `${generateDateStr(1)}T08:10:00.000Z`,
    moodId: 'happy',
    moodZone: 'green',
    physicalStates: ['normal'],
    belongings: ['school-bag', 'lunch-box', 'water-bottle', 'change-clothes'],
    notes: 'Very cheerful this morning, ready to engage',
    notifyParent: false,
    notificationChannels: [],
    notificationSent: false,
    createdBy: 'Ms. Emily Watson',
  },
  {
    id: 'pi-004',
    studentId: 'STU-2024-005',
    studentName: 'Ethan Williams',
    className: 'Secondary - A',
    date: generateDateStr(1),
    timestamp: `${generateDateStr(1)}T08:18:00.000Z`,
    moodId: 'anxious',
    moodZone: 'yellow',
    physicalStates: ['normal'],
    belongings: ['school-bag', 'lunch-box', 'water-bottle', 'homework'],
    notes: 'Seemed anxious about upcoming test, reassured upon arrival',
    notifyParent: false,
    notificationChannels: [],
    notificationSent: false,
    createdBy: 'Ms. Emma Reyes',
  },
  {
    id: 'pi-005',
    studentId: 'STU-2024-003',
    studentName: 'Liam Chen',
    className: 'Primary - A',
    date: generateDateStr(2),
    timestamp: `${generateDateStr(2)}T08:05:00.000Z`,
    moodId: 'frustrated',
    moodZone: 'yellow',
    physicalStates: ['fresh-bruise'],
    belongings: ['school-bag', 'lunch-box', 'water-bottle', 'medication'],
    notes: 'Fresh bruise noticed on left knee, parent said he fell at home yesterday',
    notifyParent: true,
    notificationChannels: ['whatsapp', 'sms', 'email'],
    notificationSent: true,
    createdBy: 'Dr. Sarah Mitchell',
  },
];

export const notificationChannelOptions: { id: NotificationChannel; label: string; icon: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line' },
  { id: 'sms', label: 'SMS', icon: 'ri-message-line' },
  { id: 'email', label: 'Email', icon: 'ri-mail-line' },
  { id: 'app', label: 'App Notification', icon: 'ri-smartphone-line' },
];

export function generateNotificationMessage(log: PreInspectionLog): string {
  const mood = getMoodById(log.moodId);
  const zone = getZoneById(log.moodZone);
  const moodText = mood ? `${mood.emoji} ${mood.label}` : 'Not recorded';
  const zoneText = zone ? `(${zone.label})` : '';
  const notesLine = log.notes ? `\n📝 Notes: ${log.notes}` : '';
  const physicalLine = log.physicalStates.length > 0
    ? `\n🔍 Physical check: ${log.physicalStates.map((ps) => {
      const opt = physicalStateOptions.find((o) => o.id === ps);
      return opt ? opt.label : ps;
    }).join(', ')}`
    : '';

  return `📋 *Pre-Inspection Update — ${log.studentName}*

Date: ${log.date}
Time: ${new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}

🧠 Mood: ${moodText} ${zoneText}${physicalLine}

📦 Belongings: ${log.belongings.length > 0 ? log.belongings.map((b) => {
    const opt = belongingOptions.find((o) => o.id === b);
    return opt ? opt.label : b;
  }).join(', ') : 'None recorded'}${notesLine}

— ${log.createdBy}, School Transit Team`;
}

// Incident types
export interface IncidentType {
  id: string;
  label: string;
  icon: string;
  severityLevels: string[];
}

export interface IncidentLog {
  id: string;
  studentId: string;
  studentName: string;
  className: string;
  date: string;
  timestamp: string;
  incidentTypeId: string;
  severity: string;
  location: string;
  description: string;
  actionsTaken: string;
  staffInvolved: string[];
  parentNotified: boolean;
  notificationChannels: NotificationChannel[];
  followUpRequired: boolean;
  followUpNotes: string;
  createdBy: string;
}

export const incidentTypes: IncidentType[] = [
  { id: 'behavioral', label: 'Behavioral Incident', icon: 'ri-emotion-line', severityLevels: ['Minor', 'Moderate', 'Major', 'Critical'] },
  { id: 'medical', label: 'Medical Emergency', icon: 'ri-heart-pulse-line', severityLevels: ['Minor', 'Moderate', 'Severe', 'Critical'] },
  { id: 'accident', label: 'Accident / Injury', icon: 'ri-first-aid-kit-line', severityLevels: ['Minor', 'Moderate', 'Serious', 'Critical'] },
  { id: 'conflict', label: 'Peer Conflict', icon: 'ri-user-unfollow-line', severityLevels: ['Minor', 'Moderate', 'Major', 'Critical'] },
  { id: 'elopement', label: 'Elopement / Wandering', icon: 'ri-run-line', severityLevels: ['Attempt', 'Brief', 'Extended', 'Critical'] },
  { id: 'sensory', label: 'Sensory Overload', icon: 'ri-flashlight-line', severityLevels: ['Minor', 'Moderate', 'Severe', 'Critical'] },
  { id: 'other-incident', label: 'Other Incident', icon: 'ri-more-line', severityLevels: ['Minor', 'Moderate', 'Major', 'Critical'] },
];

export const incidentLocations: string[] = [
  'Classroom',
  'Playground',
  'Corridor / Hallway',
  'Toilet / Washroom',
  'Therapy Room',
  'Dining Hall',
  'School Bus / Transit',
  'Entry / Gate Area',
  'Other',
];

export function getIncidentTypeById(id: string): IncidentType | undefined {
  return incidentTypes.find((t) => t.id === id);
}

export const incidentLogs: IncidentLog[] = [
  {
    id: 'inc-001',
    studentId: 'STU-2024-005',
    studentName: 'Ethan Williams',
    className: 'Secondary - A',
    date: generateDateStr(1),
    timestamp: `${generateDateStr(1)}T10:30:00.000Z`,
    incidentTypeId: 'behavioral',
    severity: 'Moderate',
    location: 'Classroom',
    description: 'Ethan became agitated during math lesson and threw his workbook across the room. He was redirected to the calm-down corner.',
    actionsTaken: 'Staff guided student to calm-down corner. Breathing exercises administered. Student returned to class after 15 minutes.',
    staffInvolved: ['Ms. Emma Reyes', 'Dr. Sarah Mitchell'],
    parentNotified: true,
    notificationChannels: ['whatsapp', 'email'],
    followUpRequired: true,
    followUpNotes: 'Schedule parent meeting to discuss triggers and coping strategies',
    createdBy: 'Ms. Emma Reyes',
  },
  {
    id: 'inc-002',
    studentId: 'STU-2024-003',
    studentName: 'Liam Chen',
    className: 'Primary - A',
    date: generateDateStr(2),
    timestamp: `${generateDateStr(2)}T14:15:00.000Z`,
    incidentTypeId: 'accident',
    severity: 'Minor',
    location: 'Playground',
    description: 'Liam tripped while running on the playground and scraped his right knee. Minor bleeding, no other injuries.',
    actionsTaken: 'First aid administered — wound cleaned and bandaged. Student monitored for 30 minutes. No signs of concussion or other injury.',
    staffInvolved: ['Dr. Sarah Mitchell'],
    parentNotified: true,
    notificationChannels: ['sms', 'email'],
    followUpRequired: false,
    followUpNotes: '',
    createdBy: 'Dr. Sarah Mitchell',
  },
  {
    id: 'inc-003',
    studentId: 'STU-2024-009',
    studentName: 'Mohammed Ayaan',
    className: 'Secondary - A',
    date: generateDateStr(0),
    timestamp: `${generateDateStr(0)}T11:20:00.000Z`,
    incidentTypeId: 'conflict',
    severity: 'Minor',
    location: 'Corridor / Hallway',
    description: 'Mohammed was involved in a verbal argument with a peer during break time. Staff intervened and both students were separated.',
    actionsTaken: 'Both students spoken to individually. Conflict resolved through mediation. Parents informed of the incident.',
    staffInvolved: ['Ms. Emma Reyes'],
    parentNotified: true,
    notificationChannels: ['whatsapp'],
    followUpRequired: false,
    followUpNotes: '',
    createdBy: 'Ms. Emma Reyes',
  },
  {
    id: 'inc-004',
    studentId: 'STU-2024-001',
    studentName: 'Aarav Mehta',
    className: 'Pre Primary - A',
    date: generateDateStr(0),
    timestamp: `${generateDateStr(0)}T09:45:00.000Z`,
    incidentTypeId: 'sensory',
    severity: 'Moderate',
    location: 'Classroom',
    description: 'Aarav experienced sensory overload during group activity due to loud noises. He covered his ears and started crying. Removed to quiet corner.',
    actionsTaken: 'Student taken to sensory room. Noise-cancelling headphones provided. Calming sensory activities administered. Student calmed after 20 minutes and returned to class.',
    staffInvolved: ['Ms. Emily Watson', 'Dr. Sarah Mitchell'],
    parentNotified: true,
    notificationChannels: ['whatsapp', 'email'],
    followUpRequired: true,
    followUpNotes: 'Review sensory accommodations in IEP. Consider noise-cancelling headphones for group activities.',
    createdBy: 'Ms. Emily Watson',
  },
];